import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Crown, Zap, Settings, ExternalLink, AlertCircle } from 'lucide-react'
import { Card, Button, AnimatedLine } from '../UI'
import { openCustomerPortal } from '../../services/stripe'
import { PLANS } from '../../config/pricing'

const SubscriptionCard = ({ subscription, loading }) => {
  const [portalLoading, setPortalLoading] = useState(false)
  const [error, setError] = useState(null)

  const plan = subscription?.plan || 'free'
  const planConfig = PLANS[plan] || PLANS.free
  const isActive = subscription?.subscriptionStatus === 'active'
  const hasSubscription = plan !== 'free' && subscription?.subscriptionId

  const handleManageSubscription = async () => {
    try {
      setError(null)
      setPortalLoading(true)
      await openCustomerPortal()
    } catch (err) {
      console.error('Failed to open portal:', err)
      setError(err.message)
      setPortalLoading(false)
    }
  }

  const formatDate = (date) => {
    if (!date) return null
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date)
  }

  const getPlanIcon = () => {
    if (plan === 'pro') return <Crown className="w-5 h-5 text-yellow-400" />
    if (plan === 'basic') return <Zap className="w-5 h-5 text-accent-400" />
    return <Settings className="w-5 h-5 text-dark-400" />
  }

  const getStatusBadge = () => {
    if (!hasSubscription) return null
    
    const statusColors = {
      active: 'bg-green-500/10 text-green-400 border-green-500/20',
      past_due: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      canceled: 'bg-red-500/10 text-red-400 border-red-500/20',
      trialing: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    }

    const statusLabels = {
      active: 'Active',
      past_due: 'Past Due',
      canceled: 'Canceled',
      trialing: 'Trial',
    }

    const status = subscription?.subscriptionStatus || 'active'
    
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[status] || statusColors.active}`}>
        {statusLabels[status] || status}
      </span>
    )
  }

  if (loading) {
    return (
      <Card>
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-4">
            <div className="h-6 bg-dark-700 rounded w-24" />
            <div className="h-5 bg-dark-700 rounded w-16" />
          </div>
          <div className="h-10 bg-dark-700 rounded w-20 mb-4" />
          <div className="h-4 bg-dark-700 rounded w-32 mb-4" />
          <div className="h-10 bg-dark-700 rounded w-full" />
        </div>
      </Card>
    )
  }

  return (
    <Card className="relative overflow-hidden">
      {/* Background gradient for Pro */}
      {plan === 'pro' && (
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-transparent pointer-events-none" />
      )}
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {getPlanIcon()}
            <span className="text-dark-400 text-sm font-medium">Current Plan</span>
          </div>
          {getStatusBadge()}
        </div>

        {/* Plan Name */}
        <h3 className="text-3xl font-bold mb-1">
          {planConfig.name}
        </h3>
        
        {/* Plan Price */}
        {plan !== 'free' && (
          <p className="text-dark-400 mb-4">
            ${planConfig.price / 100}/month
          </p>
        )}

        {/* Renewal Date */}
        {hasSubscription && subscription?.subscriptionRenewalDate && (
          <p className="text-sm text-dark-500 mb-4">
            {isActive ? 'Renews' : 'Expires'} {formatDate(subscription.subscriptionRenewalDate)}
          </p>
        )}

        {/* Admin Override Notice */}
        {subscription?.planSource === 'admin_override' && (
          <div className="flex items-center gap-2 text-sm text-accent-400 mb-4">
            <AlertCircle className="w-4 h-4" />
            <span>Plan granted by admin</span>
          </div>
        )}

        {/* Animated Line */}
        <div className="w-16 mb-4">
          <AnimatedLine trigger={true} color={plan === 'pro' ? 'gradient' : 'accent'} />
        </div>

        {/* Error Message */}
        {error && (
          <p className="text-red-400 text-sm mb-4">{error}</p>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2">
          {hasSubscription ? (
            <Button
              variant="outline"
              onClick={handleManageSubscription}
              loading={portalLoading}
              className="flex-1"
            >
              <ExternalLink className="w-4 h-4" />
              Manage Subscription
            </Button>
          ) : (
            <Link to="/pricing" className="flex-1">
              <Button fullWidth>
                <Zap className="w-4 h-4" />
                Upgrade Plan
              </Button>
            </Link>
          )}
        </div>
      </div>
    </Card>
  )
}

export default SubscriptionCard
